import React, { useCallback, useEffect, useState } from 'react';
import { HsvColor } from 'react-colorful';

import {
  hexToHsv,
  hsvToHex,
  hsvToRgb,
  hsvToHsl,
  rgbToHsv,
  hslToHsv
} from '../../../../../../../../utils/colorUtils';
import { isHexColor } from '../../../../../../../../utils/isHexColor';

export type FormatType = 'hex' | 'rgb' | 'hsl';

interface UseModelViewerColorPickerOptions {
  isOpen: boolean;
  initialColor: string;
  onChangePreview: (hex: string) => void;
  onConfirm: (hex: string) => void;
  onCancel: () => void;
}

function useModelViewerColorPicker({
  isOpen,
  initialColor,
  onChangePreview,
  onConfirm,
  onCancel
}: UseModelViewerColorPickerOptions) {
  const [hsv, setHsv] = useState<HsvColor>(() => hexToHsv(initialColor));
  const [format, setFormat] = useState<FormatType>('hex');
  const [hexInput, setHexInput] = useState(initialColor);

  useEffect(() => {
    if (isOpen) {
      setHsv(hexToHsv(initialColor));
      setHexInput(initialColor);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePickerChange = useCallback(
    (newHsv: HsvColor) => {
      setHsv(newHsv);
      const hex = hsvToHex(newHsv.h, newHsv.s, newHsv.v);
      setHexInput(hex);
      onChangePreview(hex);
    },
    [onChangePreview]
  );

  const handleHexInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setHexInput(value);
      const clean = value.startsWith('#') ? value : `#${value}`;
      if (isHexColor(clean)) {
        setHsv(hexToHsv(clean));
        onChangePreview(clean);
      }
    },
    [onChangePreview]
  );

  const handleRgbInput = useCallback(
    (channel: 'r' | 'g' | 'b', value: string) => {
      const current = hsvToRgb(hsv.h, hsv.s, hsv.v);
      const num = Math.max(0, Math.min(255, parseInt(value) || 0));
      const newHsv = rgbToHsv(
        channel === 'r' ? num : current.r,
        channel === 'g' ? num : current.g,
        channel === 'b' ? num : current.b
      );
      setHsv(newHsv);
      const hex = hsvToHex(newHsv.h, newHsv.s, newHsv.v);
      setHexInput(hex);
      onChangePreview(hex);
    },
    [hsv, onChangePreview]
  );

  const handleHslInput = useCallback(
    (channel: 'h' | 's' | 'l', value: string) => {
      const current = hsvToHsl(hsv.h, hsv.s, hsv.v);
      const max = channel === 'h' ? 360 : 100;
      const num = Math.max(0, Math.min(max, parseInt(value) || 0));
      const newHsv = hslToHsv(
        channel === 'h' ? num : current.h,
        channel === 's' ? num : current.s,
        channel === 'l' ? num : current.l
      );
      setHsv(newHsv);
      const hex = hsvToHex(newHsv.h, newHsv.s, newHsv.v);
      setHexInput(hex);
      onChangePreview(hex);
    },
    [hsv, onChangePreview]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(hsvToHex(hsv.h, hsv.s, hsv.v));
  }, [hsv, onConfirm]);

  const handleCancel = useCallback(() => {
    onChangePreview(initialColor);
    onCancel();
  }, [initialColor, onChangePreview, onCancel]);

  const rgb = hsvToRgb(hsv.h, hsv.s, hsv.v);
  const hsl = hsvToHsl(hsv.h, hsv.s, hsv.v);

  return {
    hsv,
    format,
    hexInput,
    rgb,
    hsl,
    setFormat,
    handlePickerChange,
    handleHexInput,
    handleRgbInput,
    handleHslInput,
    handleConfirm,
    handleCancel
  };
}

export default useModelViewerColorPicker;
